// What the assistant is told about the open Smart CMS section: that section's own instructions, plus how to link to records
import { buildCMSSystemPrompt } from "./articles/prompt"
import { buildServiceSystemPrompt } from "./serviceMap/prompt"
import { buildSiteConfigSystemPrompt } from "./siteConfig/prompt"
import * as cmsStore from "@/pages/dev/smart-cms/articles/store"
import * as svcStore from "@/pages/dev/smart-cms/service-map/store"
import { displayName } from "@/pages/dev/smart-cms/service-map/types"
import * as siteconfigStore from "@/pages/dev/smart-cms/site-config/store"
import type { SmartCmsAssistantDeps } from "./index"

// Edits to existing records wait as unpublished changes until a manager publishes them
const PUBLISHING = `

PUBLISHING
- Changes to existing records are saved as unpublished changes: the public site and agents keep seeing the live version until a manager publishes them in the Smart CMS. New records and deletions take effect straight away.
- So after editing, say the change is saved and waiting to be published, never that it's live. This includes setting a status or publish state to published: that is also an unpublished change until a manager publishes it.
- Only managers publish. When a manager asks, publish with workflow_publish_changes, one record per call; never publish without being asked. For anyone else, offer workflow_request_review, and point them to the Review queue in the sidebar.`

// Reviews, owners, filters and media, shared by every section
const WORKFLOW = `

REVIEWS, FILTERS AND MEDIA
- workflow_review_queue lists changes not live yet, review requests for this user, and live content due for an accuracy check. workflow_record_details shows one record's owner, review interval, last check and review request.
- workflow_show_changes shows what changed, field by field: changes not live yet against the live version, or a version against the one before it.
- workflow_set_owner sets an owner and how often a record is checked (3, 6 or 12 months, or never). Use workflow_mark_verified only after the user confirms the record is still accurate.
- To show the user a set of records, set the list's filters: call workflow_get_filters for the keys and values, then workflow_set_filters. Say what you filtered to. workflow_save_view saves the current filters under a name.
- Before asking for an image upload, check media_library for one the team already has, and use its URL.`

// Editing one language leaves the others out of date; the user decides what happens to them
const TRANSLATION_FOLLOW_UP = `

KEEPING LANGUAGES IN STEP
- When you change the wording of a record in one language and it has text in other languages, those translations no longer match. After the change, ask the user with ask_user whether to update them, naming the languages, e.g. options "Update all (Spanish, Arabic)", "Choose languages", "Leave them".
- Only wording counts. Shared fields (status, contacts, coordinates, hours, links between records) are the same in every language and need no follow-up.
- If they choose to update: carry over the change itself rather than re-translating everything around it, write each language, and leave those translations unreviewed.
- Ask once per request: for several records changed together, ask one question covering all of them. If the user already said what to do about other languages, don't ask.`

// Links in replies open the record in the Smart CMS editor
const LINK_NOTES: Record<SmartCmsAssistantDeps["backend"], string> = {
  cms: "\n\nLink to articles, sections and categories as [Title](cms:<id>); the link opens it in the editor.",
  services: "\n\nLink to records as [Name](/smart-cms?open=services/<kind>:<id>), e.g. /smart-cms?open=services/service:01H...; the link opens it in the editor.",
  siteconfig: "",
}

export function sectionInstructions({
  backend, cms, svc, siteconfig, getSheet,
  siteconfigCountries, siteconfigRegions, siteconfigCities, siteconfigArticleCategories,
}: Omit<SmartCmsAssistantDeps, "access" | "setEditorOpen" | "workflow">): string {
  const isSvc = backend === "services"
  const isSiteConfig = backend === "siteconfig"
  const instructions = isSiteConfig
      ? buildSiteConfigSystemPrompt({
          locale: siteconfig.locale,
          defaultLocale: siteconfig.store.defaultLocale,
          locales: svc.locales,
          stats: (() => {
            const st = siteconfigStore.stats(siteconfig.store)
            return { sites: st.sites, headerMenuItems: st.headerMenuItems, footerLinks: st.footerLinks, pageContents: st.pageContents }
          })(),
          countries: siteconfigCountries,
          regions: siteconfigRegions,
          cities: siteconfigCities,
          articleCategories: siteconfigArticleCategories,
        })
      : isSvc
        ? buildServiceSystemPrompt({
            locale: svc.locale,
            defaultLocale: svc.store.defaultLocale,
            locales: svc.locales,
            stats: svc.stats,
            coverage: svcStore.localeCoverage(svc.store),
            countries: Object.values(svc.store.countries).map(c => ({ id: c.id, name: displayName(c, svc.locale, svc.store.defaultLocale) })),
            skills: [],
            uploadedSheet: getSheet()
              ? { name: getSheet().activeSheet || "sheet", rowCount: getSheet().rows.length, headers: getSheet().headers }
              : null,
            nativeSearch: false,
          })
        : buildCMSSystemPrompt({
            locale: cms.locale,
            defaultLocale: cms.store.defaultLocale,
            locales: cms.store.locales,
            stats: cmsStore.stats(cms.store),
            skills: [],
            nativeSearch: false,
          })
  return instructions + TRANSLATION_FOLLOW_UP + PUBLISHING + WORKFLOW + LINK_NOTES[backend]
}
